/**
 * Remediation pricing config (source of truth for the Subscriptions UI)
 *
 * Prices per docs/Remediation_Tiers_Units_Pricing_Answers_For_Doug.md:
 *   - USD is the master price book; AUD is a separate fixed regional price (not FX/same-numeral).
 *   - Rescore Full is the exception: same-numeral $99 AUD/USD.
 *
 * Buy / Rescore call billing-gateway with the productCode on each pack (BOOST_5, RESCORE_FULL, …).
 * The worker owns the Stripe price. This file only holds display prices and product codes.
 */

export type Currency = "USD" | "AUD";

export interface PricePair {
  usd: number;
  aud: number;
}

export interface RemediationTier {
  id: "starter" | "growth" | "scale";
  name: string;
  monthly: PricePair;
  setup: PricePair;
  units: number;
  sites: number;
  urlsPerSite: number;
  sla: string;
  highlight?: boolean;
  /** Worker create-session product code for the monthly tier. */
  productCode: "STARTER" | "GROWTH" | "SCALE";
}

export interface BoosterPack {
  id: string;
  units: number;
  price: PricePair;
  /** Best per-unit value badge, e.g. "Best value". */
  badge?: string;
  /** Worker create-session product code. Not a Stripe price id. */
  productCode: "BOOST_5" | "BOOST_15" | "BOOST_30" | "BOOST_100";
}

export interface RescoreProduct {
  id: "rescore-full";
  name: string;
  description: string;
  /** Same-numeral: usd === aud. */
  price: PricePair;
  /** Worker create-session product code. Not a Stripe price id. */
  productCode: "RESCORE_FULL";
}

export const REMEDIATION_TIERS: RemediationTier[] = [
  {
    id: "starter",
    name: "Starter",
    monthly: { usd: 299, aud: 449 },
    setup: { usd: 499, aud: 749 },
    units: 10,
    sites: 1,
    urlsPerSite: 100,
    sla: "Standard support",
    productCode: "STARTER",
  },
  {
    id: "growth",
    name: "Growth",
    monthly: { usd: 699, aud: 1049 },
    setup: { usd: 1299, aud: 1949 },
    units: 30,
    sites: 3,
    urlsPerSite: 250,
    sla: "First response within 1 business day",
    highlight: true,
    productCode: "GROWTH",
  },
  {
    id: "scale",
    name: "Scale",
    monthly: { usd: 1999, aud: 2999 },
    setup: { usd: 3999, aud: 5999 },
    units: 100,
    sites: 12,
    urlsPerSite: 500,
    sla: "4 business hours + dedicated AM",
    productCode: "SCALE",
  },
];

export const BOOSTER_PACKS: BoosterPack[] = [
  {
    id: "booster-5",
    units: 5,
    price: { usd: 199, aud: 299 },
    productCode: "BOOST_5",
  },
  {
    id: "booster-15",
    units: 15,
    price: { usd: 499, aud: 749 },
    productCode: "BOOST_15",
  },
  {
    id: "booster-30",
    units: 30,
    price: { usd: 899, aud: 1349 },
    productCode: "BOOST_30",
  },
  {
    id: "booster-100",
    units: 100,
    price: { usd: 2499, aud: 3749 },
    badge: "Best value",
    productCode: "BOOST_100",
  },
];

export const RESCORE_FULL: RescoreProduct = {
  id: "rescore-full",
  name: "Rescore Full",
  description: "Refresh your full diagnostic and remediation report to see how your scores have improved.",
  price: { usd: 99, aud: 99 },
  productCode: "RESCORE_FULL",
};

/** Approved portfolio rescore prices. NOT checkout-enabled until multi-site fulfillment ships. */
export const PORTFOLIO_RESCORES = [
  { id: "portfolio-3", label: "Growth portfolio", sites: 3, price: { usd: 249, aud: 249 }, requiredTier: "growth" },
  { id: "portfolio-12", label: "Scale portfolio", sites: 12, price: { usd: 799, aud: 799 }, requiredTier: "scale" },
] as const;

/**
 * Checkout and the billing portal go through the `billing-gateway` edge function.
 * The browser never calls og01.ai directly.
 * Product codes above are what the worker accepts.
 */

/** Formats a price for display in the chosen currency, e.g. "A$1,349" or "US$199". */
export function formatPrice(pair: PricePair, currency: Currency): string {
  const amount = currency === "AUD" ? pair.aud : pair.usd;
  const prefix = currency === "AUD" ? "A$" : "US$";
  return `${prefix}${amount.toLocaleString("en-US")}`;
}

/** Per-unit price for a booster, used to show relative value. */
export function perUnitPrice(pack: BoosterPack, currency: Currency): string {
  const amount = currency === "AUD" ? pack.price.aud : pack.price.usd;
  const prefix = currency === "AUD" ? "A$" : "US$";
  const per = amount / pack.units;
  return `${prefix}${per.toFixed(2)}/unit`;
}
